import { createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useMemo } from 'react';
import { VideoWorkspaceShell, WorkspaceMode } from '../../../components/video-workspace/VideoWorkspaceShell';
import { WorkspaceCrash } from '../../../components/video-workspace/WorkspaceCrash';
import { AssessmentWorkspaceShell } from '../../../components/assessment-workspace/AssessmentWorkspaceShell';
import { useVideoWorkspaceStore } from '../../../stores/videoWorkspaceStore';
import { conversationAPI, slidesAPI } from '../../../services/api';
import type { OutlineStatus } from '@/types/outline';
import type { ProviderType } from '../../../services/messageService';
import type { FrontDoorStart } from '../../../lib/frontDoor/handoff';
import { ALL_WORKSPACE_TABS, workspaceMode, type WorkspaceTab } from '../../../components/video-workspace/workspaceTabs';

// Router state passed from HeroScreen
interface VideoWorkspaceRouterState {
    autoSend?: boolean;
    userInstructions?: string;
    provider?: ProviderType;
    frontDoor?: FrontDoorStart;
}

// Hidden tabs are dropped so a stale ?tab= deep link falls back to the default view
// rather than opening one with no tab button to leave it by.
const WORKSPACE_TABS: WorkspaceTab[] = ALL_WORKSPACE_TABS
    .filter(t => (t !== 'narration' || import.meta.env.VITE_SHOW_NARRATION_TAB === 'true')
              && (t !== 'blog' || import.meta.env.VITE_SHOW_BLOG_TAB === 'true'));

export const Route = createFileRoute("/chat/$conversationId/video-workspace")({
    component: VideoWorkspaceRoute,
    // A crash inside the studio keeps a page with Reload instead of the router's blank default (#1095).
    errorComponent: WorkspaceCrash,
    // ?tab=lab deep-links straight to a tab — MCP tools hand these links back to the user
    // (e.g. sync_lab_asset_to_production returns a workspaceUrl pointing at the Lab).
    validateSearch: (search: Record<string, unknown>): { tab?: WorkspaceTab } => {
        const tab = search.tab;
        return typeof tab === 'string' && WORKSPACE_TABS.includes(tab as WorkspaceTab)
            ? { tab: tab as WorkspaceTab }
            : {};
    },
    loader: async ({ params }) => {
        try {
            // Validate conversation exists and fetch slides count
            const [{ conversation }, slidesResponse] = await Promise.all([
                conversationAPI.getById(params.conversationId),
                slidesAPI.getByConversation(params.conversationId).catch(() => ({ slides: [] })),
            ]);

            return {
                conversation,
                slideCount: slidesResponse.slides?.length || 0,
            };
        } catch (error) {
            console.error('Failed to load video workspace:', error);
            throw redirect({
                to: '/chat/$conversationId',
                params: { conversationId: params.conversationId }
            });
        }
    }
});

function VideoWorkspaceRoute() {
    const { conversationId } = Route.useParams();
    const { conversation, slideCount: loaderSlideCount } = Route.useLoaderData();
    const { tab: initialTab } = Route.useSearch();

    // Assessment projects reuse the VIDEO conversation lifecycle but render a lean
    // assessment view (chat + question filmstrip/preview) instead of the slide shell.
    const projectKind = (conversation?.metadata as { projectKind?: string } | null | undefined)?.projectKind;
    const isAssessment = projectKind === 'assessment';

    // Get router state (passed from HeroScreen navigation)
    const routerState = useRouterState({
        select: (state) => state.location.state as VideoWorkspaceRouterState | undefined,
    });

    // Subscribe to store for reactive outline status (SSE updates)
    // This allows mode to update when outline completes without page reload
    // IMPORTANT: Only trust store data if it matches the current conversation!
    const { outline, slides: storeSlides, conversationId: storeConversationId } = useVideoWorkspaceStore();

    // Only use store data if it's for THIS conversation (prevents cross-contamination)
    const isStoreForCurrentConversation = storeConversationId === conversationId;
    const storeOutlineStatus = isStoreForCurrentConversation ? outline?.status : undefined;
    const storeSlideCount = isStoreForCurrentConversation ? storeSlides.length : 0;

    const creationMode = (conversation?.planJson as { creationMode?: string } | null)?.creationMode;

    // Determine workspace mode based on conversation state
    // Uses loader data (initial) and store state (reactive, if matching conversation)
    const storePlanSlides = isStoreForCurrentConversation ? outline?.slides : undefined;
    const mode = useMemo((): WorkspaceMode => {
        const planJson = conversation?.planJson as { status?: OutlineStatus; slides?: Array<{ slideConversationId?: string; isDeleted?: boolean }> } | null;
        return workspaceMode({
            outlineStatus: storeOutlineStatus || planJson?.status,
            // Use max of loader and store slide count (store updates via SSE)
            slideCount: Math.max(loaderSlideCount, storeSlideCount),
            planSlides: storePlanSlides ?? planJson?.slides,
            collectionType: conversation?.collectionType,
        });
    }, [conversation, loaderSlideCount, storeOutlineStatus, storeSlideCount, storePlanSlides]);

    if (isAssessment) {
        return (
            <AssessmentWorkspaceShell
                conversationId={conversationId}
                conversationTitle={conversation?.title || undefined}
                autoSendMessage={routerState?.autoSend ? routerState?.userInstructions : undefined}
            />
        );
    }

    return (
        <VideoWorkspaceShell
            conversationId={conversationId}
            mode={mode}
            initialTab={initialTab}
            creationMode={creationMode}
            autoSend={routerState?.autoSend}
            initialMessage={routerState?.userInstructions}
            initialProvider={routerState?.provider}
            frontDoor={routerState?.frontDoor}
        />
    );
}
